import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../components/Button";
import Card from "../../components/Card";
import PageHeader from "../../components/PageHeader";
import {
  INITIAL_FORM_STATE,
  STEPS,
  normalizeApiError,
} from "./../../utils/bookingWizard";
import { useBookingData } from "./../../hooks/useBookingData";
import { useBookingValidation } from "./../../hooks/useBookingValidation";
import { StepIndicator } from "./components/StepIndicator";
import { Step1SubjectTime } from "./components/Step1SubjectTime";
import { Step2Topics } from "./components/Step2Topics";
import { Step3Tutors } from "./components/Step3Tutors";
import { FaCheck } from "react-icons/fa";
import { MAX_SELECTED_TUTORS } from "./../../constants/bookingConstants";
import { bookingService } from "./../../services/bookingService";

export default function BookingWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [subjects, setSubjects] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [enforceAvailability, setEnforceAvailability] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);

  // Data loading hooks
  const { loading, setLoading, loadSubjects, loadSettings, loadTutors } =
    useBookingData(
      formData,
      setFormData,
      setSubjects,
      setTutors,
      setEnforceAvailability
    );

  // Validation hooks
  const isTutorAvailable = useCallback(
    (tutor, { date, start, end }) => {
      if (!enforceAvailability) return true;
      const availability = tutor.profile?.availability;
      if (!availability?.length) return true;
      const day = new Date(date).getDay();
      const reqStart = Number(start.replace(":", ""));
      const reqEnd = Number(end.replace(":", ""));
      return availability.some((slot) => {
        if (slot.dayOfWeek !== day) return false;
        const slotStart = Number(slot.startTime.replace(":", ""));
        const slotEnd = Number(slot.endTime.replace(":", ""));
        return reqStart >= slotStart && reqEnd <= slotEnd;
      });
    },
    [enforceAvailability]
  );

  const {
    isFutureTimeSlot,
    getTimeSlotError,
    validateStep1,
    canProceedToStep2,
    availableTutors,
  } = useBookingValidation(
    formData,
    enforceAvailability,
    tutors,
    isTutorAvailable
  );

  // Fix: Define canSubmit here, not from the hook
  const canSubmit = useMemo(() => {
    return (
      !loading.form &&
      (formData.useRandom || formData.selectedTutorIds.length > 0)
    );
  }, [loading.form, formData.useRandom, formData.selectedTutorIds]);

  // Form handlers
  const updateForm = useCallback((patch) => {
    setFormData((prev) => ({ ...prev, ...patch }));
  }, []);

  const updateTimeSlot = useCallback((index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.preferredTimes];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, preferredTimes: updated };
    });
  }, []);

  const addTimeSlot = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      preferredTimes: [
        ...prev.preferredTimes,
        { ...INITIAL_FORM_STATE.preferredTimes[0] },
      ],
    }));
  }, []);

  const removeTimeSlot = useCallback(
    (index) => {
      if (formData.preferredTimes.length <= 1) {
        toast.warning("You need at least one time slot");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        preferredTimes: prev.preferredTimes.filter((_, i) => i !== index),
      }));
    },
    [formData.preferredTimes.length]
  );

  const duplicateTimeSlot = useCallback(
    (index) => {
      const slotToCopy = { ...formData.preferredTimes[index] };
      setFormData((prev) => ({
        ...prev,
        preferredTimes: [...prev.preferredTimes, slotToCopy],
      }));
    },
    [formData.preferredTimes]
  );


  const toggleTutor = useCallback((tutorId) => {
    setFormData((prev) => {
      const selected = prev.selectedTutorIds;
      if (selected.includes(tutorId)) {
        return {
          ...prev,
          selectedTutorIds: selected.filter((id) => id !== tutorId),
        };
      }
      if (selected.length >= MAX_SELECTED_TUTORS) {
        toast.warning(`You can select up to ${MAX_SELECTED_TUTORS} tutors only`);
        return prev;
      }
      return { ...prev, selectedTutorIds: [...selected, tutorId] };
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const { isValid, errors } = validateStep1();
    if (!isValid) {
      setFieldErrors(errors);
      toast.error("Please fix the errors before submitting");
      return;
    }

    setLoading((prev) => ({ ...prev, form: true }));
    try {
      if (!formData.preferredTimes.every(isFutureTimeSlot)) {
        throw new Error("Invalid time slots selected");
      }

      await bookingService.create({
        subjectId: formData.subjectId,
        preferredTimes: formData.preferredTimes,
        selectedTutorIds: formData.useRandom ? [] : formData.selectedTutorIds,
        sessionType: formData.sessionType,
        location:
          formData.sessionType === "in-person" ? formData.location : null,
        topicsToFocus: formData.topicsToFocus,
      });

      toast.success("Booking request created successfully!");
      navigate("/student/dashboard");
    } catch (err) {
      const msg = normalizeApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading((prev) => ({ ...prev, form: false }));
    }
  };

  // Step navigation
  const nextStep = () => {
    if (step === 1) {
      const { isValid } = validateStep1();
      if (isValid) {
        setStep(2);
      } else {
        toast.error("Please fix the errors before continuing");
      }
    } else {
      setStep(step + 1);
    }
  };

  const prevStep = () => setStep(step - 1);

  // Load tutors when entering step 3
  useMemo(() => {
    if (step === 3 && formData.subjectId) {
      loadTutors();
    }
  }, [step, formData.subjectId, loadTutors]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
      <div className="mb-8">
        <PageHeader
          title="Create Booking Request"
          subtitle="Follow the steps below to book a tutoring session"
        />
      </div>

      <Card className="mb-6 overflow-hidden">
        <div className="p-4 sm:p-6">
          <StepIndicator step={step} steps={STEPS} formData={formData} />
          <div className="flex items-center justify-between mt-4">
            {STEPS.map(({ number, title, icon: Icon }, index) => (
              <div key={number} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold ${step === number
                      ? "bg-primary text-white ring-4 ring-primary/20"
                      : step > number
                        ? "bg-green-500 text-white"
                        : "bg-surface border-2 border-border text-text/40"
                      }`}
                  >
                    {step > number ? (
                      <FaCheck className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <div className="mt-2 text-center">
                    <p
                      className={`text-xs sm:text-sm font-semibold ${step === number ? "text-primary" : "text-text/60"
                        }`}
                    >
                      {title}
                    </p>
                  </div>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`h-0.5 w-full mx-2 ${step > number ? "bg-green-500" : "bg-border"
                      }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        <form onSubmit={handleSubmit} className="p-6 sm:p-8">
          {step === 1 && (
            <Step1SubjectTime
              formData={formData}
              subjects={subjects}
              loading={loading}
              fieldErrors={fieldErrors}
              updateForm={updateForm}
              updateTimeSlot={updateTimeSlot}
              addTimeSlot={addTimeSlot}
              removeTimeSlot={removeTimeSlot}
              duplicateTimeSlot={duplicateTimeSlot}
              canProceedToStep2={canProceedToStep2}
              nextStep={nextStep}
              getTimeSlotError={getTimeSlotError}
              isFutureTimeSlot={isFutureTimeSlot}
            />
          )}

          {step === 2 && (
            <Step2Topics
              formData={formData}
              updateForm={updateForm}
              prevStep={prevStep}
              nextStep={nextStep}
            />
          )}

          {step === 3 && (
            <Step3Tutors
              formData={formData}
              loading={loading}
              enforceAvailability={enforceAvailability}
              availableTutors={availableTutors}
              updateForm={updateForm}
              toggleTutor={toggleTutor}
              prevStep={prevStep}
              canSubmit={canSubmit}
            />
          )}
        </form>
      </Card>
    </div>
  );
}
